import React, { useRef } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { Box, xcss } from '@atlaskit/primitives';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { useHeartbeatsContext } from '../../../controllers/providers/heartbeats';
import { heartbeatsUserRightsResource } from '../../../services/resources';

import messages from './messages';
import { type Props } from './types';

const ContainerStyles = xcss({ width: 'fit-content' });

export const Action = ({ heartbeat, onDeleteClick, onSwitchClick }: Props) => {
	const { focusState } = useHeartbeatsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: rights } = useResource(heartbeatsUserRightsResource);

	const flagApi = useFlags();
	const { formatMessage } = useIntl();
	const dropdownButtonRef = useRef<HTMLButtonElement>(null);

	function copyToClipboard() {
		const { clipboard } = window.navigator;

		clipboard.writeText(heartbeat.heartbeatEmailAddress).then(() => {
			flagApi.showFlag({
				...successToastProps(),
				title: formatMessage(messages.copySuccess),
			});
		});
	}

	assert(
		!!rights,
		new AssertionError(
			'Action',
			'User right should be set in parent component, but somehow it is not set.',
		),
	);

	return (
		<Box onClick={(e: React.MouseEvent) => e.stopPropagation()} xcss={ContainerStyles}>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						label={formatMessage(messages.moreAriaLabel, {
							name: heartbeat.name,
						})}
						{...props}
						icon={MoreIcon}
						ref={mergeRefs([triggerRef, dropdownButtonRef])}
						appearance="subtle"
					/>
				)}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					<DropdownItem onClick={copyToClipboard}>
						<FormattedMessage {...messages.copy} />
					</DropdownItem>
					{rights.hasEditRight && (
						<>
							<DropdownItem
								onClick={() => {
									const event = createAnalyticsEvent({});
									fireUIAnalytics(event, 'button clicked', 'dropdownMenuEditButton');

									focusState.open({ data: heartbeat, mode: 'edit', returnRef: dropdownButtonRef });
								}}
							>
								<FormattedMessage {...messages.edit} />
							</DropdownItem>
							<DropdownItem
								onClick={() => {
									focusState.open({
										data: { ...heartbeat, name: '' },
										mode: 'duplicate',
										returnRef: dropdownButtonRef,
									});
								}}
							>
								<FormattedMessage {...messages.duplicate} />
							</DropdownItem>
							<DropdownItem onClick={onSwitchClick}>
								{heartbeat.status === 'Off' ? (
									<FormattedMessage {...messages.turnon} />
								) : (
									<FormattedMessage {...messages.turnoff} />
								)}
							</DropdownItem>
						</>
					)}
					{rights.hasDeleteRight && (
						<DropdownItem onClick={onDeleteClick}>
							<FormattedMessage {...messages.delete} />
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		</Box>
	);
};
